/**
 * Catalogo dei permessi lato client.
 *
 * ATTENZIONE — Questi controlli servono soltanto a **non mostrare** comandi che
 * l'utente non può usare. Non sono un meccanismo di sicurezza: l'autorizzazione
 * vera è applicata dalle policy Row-Level Security e dai controlli espliciti
 * dentro le funzioni PostgreSQL. Se questo file venisse manomesso nel browser,
 * il database rifiuterebbe comunque l'operazione.
 */

export const PERMISSIONS = {
  orgView: 'org.view',
  orgManage: 'org.manage',
  orgManageBilling: 'org.manage_billing',
  locationView: 'location.view',
  locationManage: 'location.manage',
  userView: 'user.view',
  userManage: 'user.manage',
  roleManage: 'role.manage',
  floorView: 'floor.view',
  floorManage: 'floor.manage',
  floorOperate: 'floor.operate',
  reservationView: 'reservation.view',
  reservationCreate: 'reservation.create',
  reservationUpdate: 'reservation.update',
  reservationCancel: 'reservation.cancel',
  customerView: 'customer.view',
  customerManage: 'customer.manage',
  customerExport: 'customer.export',
  customerErase: 'customer.erase',
  menuView: 'menu.view',
  menuManage: 'menu.manage',
  menuAvailability: 'menu.availability',
  orderView: 'order.view',
  orderCreate: 'order.create',
  orderUpdate: 'order.update',
  orderCancelItem: 'order.cancel_item',
  orderCancel: 'order.cancel',
  orderTransfer: 'order.transfer',
  orderDiscount: 'order.discount',
  orderClose: 'order.close',
  kitchenView: 'kitchen.view',
  kitchenOperate: 'kitchen.operate',
  paymentView: 'payment.view',
  paymentCreate: 'payment.create',
  paymentRefund: 'payment.refund',
  cashOpen: 'cash.open',
  cashClose: 'cash.close',
  cashMovement: 'cash.movement',
  inventoryView: 'inventory.view',
  inventoryManage: 'inventory.manage',
  inventoryCostView: 'inventory.cost_view',
  purchasingView: 'purchasing.view',
  purchasingManage: 'purchasing.manage',
  recipeView: 'recipe.view',
  recipeManage: 'recipe.manage',
  staffView: 'staff.view',
  staffManage: 'staff.manage',
  staffCostView: 'staff.cost_view',
  staffAttendance: 'staff.attendance',
  reportView: 'report.view',
  reportFinancial: 'report.financial',
  reportExport: 'report.export',
  auditView: 'audit.view',
  settingsManage: 'settings.manage',
  integrationManage: 'integration.manage',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Etichette in italiano dei ruoli di sistema. */
export const ROLE_LABELS: Record<string, string> = {
  owner: 'Proprietario',
  admin: 'Amministratore',
  location_manager: 'Responsabile di sede',
  floor_manager: 'Responsabile di sala',
  waiter: 'Cameriere',
  cashier: 'Cassiere',
  chef: 'Chef',
  kitchen_staff: 'Operatore cucina',
  storekeeper: 'Magazziniere',
  delivery: 'Addetto consegne',
  accountant: 'Revisore contabile',
};

/**
 * Sezione dell'applicazione in cui il ruolo lavora abitualmente: determina dove
 * atterra dopo il login. Un cuoco non deve passare dalla dashboard direzionale
 * per arrivare al proprio schermo.
 */
export const ROLE_HOME_ROUTE: Record<string, string> = {
  owner: '/app/dashboard',
  admin: '/app/dashboard',
  location_manager: '/app/dashboard',
  floor_manager: '/app/sala',
  waiter: '/app/sala',
  cashier: '/app/cassa',
  chef: '/app/cucina',
  kitchen_staff: '/app/cucina',
  storekeeper: '/app/magazzino',
  delivery: '/app/ordini',
  accountant: '/app/report',
};

/**
 * Aree del catalogo permessi (colonna `permissions.module`), nell'ordine in cui
 * si presentano quando si compone un ruolo: prima il servizio, poi la gestione.
 */
export const PERMISSION_MODULES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'floor', label: 'Sala e tavoli' },
  { code: 'orders', label: 'Ordini' },
  { code: 'kitchen', label: 'Cucina' },
  { code: 'payments', label: 'Pagamenti e cassa' },
  { code: 'reservations', label: 'Prenotazioni' },
  { code: 'customers', label: 'Clienti' },
  { code: 'menu', label: 'Menu' },
  { code: 'inventory', label: 'Magazzino' },
  { code: 'purchasing', label: 'Acquisti' },
  { code: 'recipes', label: 'Ricette' },
  { code: 'staff', label: 'Personale' },
  { code: 'reports', label: 'Report' },
  { code: 'identity', label: 'Utenti e ruoli' },
  { code: 'locations', label: 'Sedi' },
  { code: 'organizations', label: 'Organizzazione' },
  { code: 'settings', label: 'Impostazioni' },
  { code: 'audit', label: 'Registro operazioni' },
];

/**
 * Per i ruoli personalizzati la schermata iniziale si deduce dai permessi: il
 * primo lavoro operativo che il ruolo può svolgere, nell'ordine del servizio.
 */
const HOME_BY_PERMISSION: ReadonlyArray<{ permission: Permission; route: string }> = [
  { permission: PERMISSIONS.floorView, route: '/app/sala' },
  { permission: PERMISSIONS.kitchenView, route: '/app/cucina' },
  { permission: PERMISSIONS.cashOpen, route: '/app/cassa' },
  { permission: PERMISSIONS.inventoryView, route: '/app/magazzino' },
  { permission: PERMISSIONS.reportView, route: '/app/report' },
  { permission: PERMISSIONS.orderView, route: '/app/ordini' },
];

export function homeRouteFor(roleCode: string, permissions: readonly string[]): string {
  const fixed = ROLE_HOME_ROUTE[roleCode];
  if (fixed) return fixed;
  return HOME_BY_PERMISSION.find((entry) => permissions.includes(entry.permission))?.route ?? '/app/dashboard';
}

export function hasPermission(permissions: readonly string[], required: Permission): boolean {
  return permissions.includes(required);
}

export function hasAnyPermission(permissions: readonly string[], required: readonly Permission[]): boolean {
  return required.some((permission) => permissions.includes(permission));
}

export function hasAllPermissions(permissions: readonly string[], required: readonly Permission[]): boolean {
  return required.every((permission) => permissions.includes(permission));
}
